import { Flex, ScrollAreaAutosize } from "@mantine/core";
import { BoxTitle, Container } from "./StyledComponents";
import styled, { keyframes } from "styled-components";
import { theme } from "../../theme";
import { GiftIcon, CakeIcon } from "@heroicons/react/24/outline";
import { useEffect, useState } from "react";
import { getLiveBirthday } from "../../api/dashboardApi";
import { useFilterContext } from "../../context/DashboardFilterContext";
import dayjs from "dayjs";


function initials(name: string) {
  return (name ?? "?")
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
}


const UpcomingBirthdays = ({ flex }: { flex: any }) => {
  const { filters } = useFilterContext();
  const [employees, setEmployees] = useState<any>();

  const getBirthdays = async () => {
    try {
      const { data, success } = await getLiveBirthday(filters);
      if (success) setEmployees(data?.employees);
    } catch (error) {}
  };

  useEffect(() => {
    getBirthdays();
  }, [filters]);

  return (
    <Container $flex={flex}>
      <HeaderRow>
        <Flex align="center" gap={10}>
          <HeaderIcon>
            <GiftIcon width={18} />
          </HeaderIcon>
          <BoxTitle>Yaklaşan Doğum Günleri</BoxTitle>
        </Flex>
      </HeaderRow>

      <ScrollAreaAutosize h="90%">
        <EmpList>
          {employees?.map((employee: any, index: number) => {
            const isToday = employee?.status === 1;
            const daysLeft = employee?.daysLeft;

            return (
              <div key={index}>
                <EmpRow $delay={index * 45} $isToday={isToday}>
                  <GradAvatar>
                    {initials(employee?.name)}
                  </GradAvatar>

                  <EmpInfo>
                    <EmpName>{employee?.name}</EmpName>
                    <EmpMetaColumn>
                      <span className="dept">{employee?.department}</span>
                      <span className="date">{dayjs(employee?.birthday).format("DD MMMM")}</span>
                    </EmpMetaColumn>
                  </EmpInfo>

                  <StatusSection>
                    {isToday ? (
                      <TodayBadge>
                        <CakeIcon width={14} />
                        <span>Bugün!</span>
                      </TodayBadge>
                    ) : daysLeft > 0 ? (
                      <DaysBadge>
                        {daysLeft} gün kaldı
                      </DaysBadge>
                    ) : null}
                  </StatusSection>
                </EmpRow>
                {index !== employees?.length - 1 && <ListDivider />}
              </div>
            );
          })}
        </EmpList>
      </ScrollAreaAutosize>
    </Container>
  );
};

export default UpcomingBirthdays;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
`;


const AVATAR_COLORS = {
  from: "#f0d3e4", 
  to: "#f5afba",   
};

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const HeaderIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: ${theme.colors.brand.normal}10;
  color: ${theme.colors.brand.normal};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border: 1px solid ${theme.colors.brand.normal}20;
`;

const EmpList = styled.div`
  display: flex;
  flex-direction: column;
`;

const EmpRow = styled.div<{ $delay: number; $isToday?: boolean }>`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 10px;
  margin: 0 -10px;
  animation: ${slideUp} 0.3s ease both;
  animation-delay: ${(p) => p.$delay}ms;
  border-radius: 12px;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1); 
  cursor: pointer;
  position: relative;

  ${(p) => p.$isToday && `
    background: #fff5f780;
  `}

  &:hover {
    transform: translateY(-4px) scale(1.025); 
    
    background: #ffffff;
    box-shadow: 
      0 12px 20px -5px rgba(0, 0, 0, 0.1),
      0 8px 10px -6px rgba(0, 0, 0, 0.05);
    
    z-index: 10; 
  }

  &:active {
    transform: translateY(-1px) scale(0.98);
  }
`;

const GradAvatar = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 30px;
  background: linear-gradient(135deg, ${AVATAR_COLORS.from}, ${AVATAR_COLORS.to});
  color: white;
  font-size: 14px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
`;

const EmpInfo = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

const EmpName = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #0f172a;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const EmpMetaColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  margin-top: 2px;

  .dept {
    font-size: 12px;
    color: #64748b;
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .date {
    font-size: 11px;
    color: #94a3b8;
    font-weight: 400;
  }
`;

const StatusSection = styled.div`
  flex-shrink: 0;
`;

const TodayBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: #fdf2f8;
  border: 1px solid #fbcfe8;
  border-radius: 50px;
  color: #db2777;
  font-size: 12.5px;
  font-weight: 700;
`;

const DaysBadge = styled.div`
  padding: 4px 10px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 50px;
  color: #64748b;
  font-size: 11.5px;
  font-weight: 600;
`;

const ListDivider = styled.div`
  height: 1px;
  background: #f1f5f9;
`;